export type IndustryTransitionImpactIndustryDto = {
    readonly industry: string;
    readonly taxonomyPaths: readonly string[];
};

export type IndustryTransitionImpactRowDto = {
    readonly assetId: string;
    readonly assetName: string;
    readonly ownerName?: string | null;
    readonly newCompaniesEstimationDelta?: number | null;
    readonly isAccountLevel?: boolean | null;
    readonly industries: readonly IndustryTransitionImpactIndustryDto[];
};

export type IndustryTransitionImpactReportDto = {
    /** `saveSearches`, not `savedSearches` — the backend spelling, confined to this type and the adapter. */
    readonly saveSearches: readonly IndustryTransitionImpactRowDto[];
    readonly territories: readonly IndustryTransitionImpactRowDto[];
    readonly leadScoringRules: readonly IndustryTransitionImpactRowDto[];
    /** CRM jobs the switch leaves alone, so active integrations keep running. */
    readonly crmJobsCount: number | null;
};

export type ImpactReportRowsDtoKey = keyof Omit<IndustryTransitionImpactReportDto, "crmJobsCount">;

export const impactedAssetGroupMap = {
    savedSearches: "savedSearches",
    territories: "territories",
    leadScoringRules: "leadScoringRules",
} as const;

export type ImpactedAssetGroup = (typeof impactedAssetGroupMap)[keyof typeof impactedAssetGroupMap];

/** Canonical order of the report's sections/tabs. The adapter and the skeleton both read it, so their tab strips can never drift. */
export const impactedAssetGroupOrder: readonly ImpactedAssetGroup[] = [
    impactedAssetGroupMap.savedSearches,
    impactedAssetGroupMap.territories,
    impactedAssetGroupMap.leadScoringRules,
];

export type IndustryTransition = {
    readonly industry: string;
    readonly taxonomyPaths: readonly string[];
};

/**
 * Absence is `undefined`, never `null`: `null` is a wire spelling, and the adapter is the boundary
 * that normalises it away so no view has to guard for both.
 */
export type ImpactedAsset = {
    /** Stable across a stale→fresh data swap, so an expanded row follows its asset rather than its index. */
    readonly id: string;
    readonly name: string;
    readonly ownerName?: string;
    readonly isAccountLevel?: boolean;
    /** Signed delta; absent when the wire value is null or zero. */
    readonly newCompaniesEstimationDelta?: number;
    readonly industries: readonly IndustryTransition[];
};

export type ImpactedAssetSection = {
    readonly group: ImpactedAssetGroup;
    readonly assets: readonly ImpactedAsset[];
};

export type IndustryTransitionImpactReport = {
    /** Ordered for the tab strip. The adapter keeps empty groups; visibility filtering drops them before render. */
    readonly sections: readonly ImpactedAssetSection[];
    readonly totalAssetCount: number;
    readonly crmJobsCount?: number;
    /**
     * Rows the backend returned with no industry filter at all. They are not impacted, so they are
     * dropped — but the count is carried out so the caller can report a contract the FE believes
     * impossible, rather than swallowing it.
     */
    readonly droppedNotImpactedCount: number;
};
